import React, { useMemo, useState } from 'react';
import popupIcon from "../../../img/icons.png";

function GameControls({
  onRestart,
  onUndo,
  canUndo,
  moveCount,
  currentLevel,
  optimalMoves,
  leaderboardIdentity,
  levelStartTimestamp,
  formatElapsedTime,
  loadLeaderboard,
}) {
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);
  const [popupKey, setPopupKey] = useState(0);
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [currentUserRank, setCurrentUserRank] = useState(null);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);

  const elapsedTime = useMemo(() => {
    if (typeof formatElapsedTime !== "function") return "00:00";
    return formatElapsedTime(Date.now() - levelStartTimestamp);
  }, [formatElapsedTime, levelStartTimestamp, popupKey]);
  const isCurrentUserInLeaderboard = useMemo(
    () => leaderboardRows.some((row) => row.isCurrentUser),
    [leaderboardRows]
  );

  const openLeaderboard = async () => {
    setPopupKey(Date.now());
    setIsLeaderboardOpen(true);
    setIsLoadingLeaderboard(true);
    try {
      const leaderboardResult = typeof loadLeaderboard === "function"
        ? await loadLeaderboard()
        : { rows: [], currentUserRank: null };
      const rows = Array.isArray(leaderboardResult)
        ? leaderboardResult
        : leaderboardResult?.rows;
      setLeaderboardRows(Array.isArray(rows) ? rows.slice(0, 10) : []);
      setCurrentUserRank(
        Array.isArray(leaderboardResult) ? null : leaderboardResult?.currentUserRank ?? null
      );
    } finally {
      setIsLoadingLeaderboard(false);
    }
  };

  return (
    <div className="game-controls">
      <div className="game-controls__top">
        <div className="game-controls__info">
          <span className="game-controls__level">Level {currentLevel}</span>
          <span className="game-controls__moves">
            Moves: {moveCount} {optimalMoves > 0 && `(Best: ${optimalMoves})`}
          </span>
        </div>
      </div>
      
      <div className="game-controls__bottom">
        <button
          className="game-controls__btn game-controls__btn--restart"
          onClick={onRestart}
          title="Restart Level"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
          </svg>
          RESTART
        </button>

        <button
          className="game-controls__btn game-controls__btn--leaderboard"
          onClick={openLeaderboard}
          title="Open Leaderboard"
          aria-label="Open Leaderboard"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M13 3v18h-2V3h2zm-7 8v10H4V11h2zm14-4v14h-2V7h2z" />
          </svg>
          LEADERBOARD
        </button>
        
        <button
          className="game-controls__btn game-controls__btn--undo"
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo Last Move"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
            <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
          </svg>
          UNDO
        </button>
      </div>

      {isLeaderboardOpen && (
        <div className="leaderboard-popup" role="dialog" aria-modal="true" aria-label="Leaderboard">
          <div
            className="leaderboard-popup__backdrop"
            aria-hidden="true"
          />
          <div className="leaderboard-popup__sheet">
            <div className="leaderboard-popup__header">
              <h3 className="leaderboard-popup__title">
                <img src={popupIcon} alt="" className="leaderboard-popup__title-icon" aria-hidden="true" />
                <span>Leaderboard</span>
              </h3>
              <button
                className="leaderboard-popup__close"
                onClick={() => setIsLeaderboardOpen(false)}
                aria-label="Close leaderboard"
              >
                ×
              </button>
            </div>

            {isLoadingLeaderboard ? (
              <p className="leaderboard-popup__empty">Loading leaderboard...</p>
            ) : leaderboardRows.length > 0 ? (
              <>
                <div className="leaderboard-popup__table-wrap">
                  <table className="leaderboard-popup__table">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Player</th>
                        <th>Level</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboardRows.map((row, index) => (
                        <tr
                          key={`${row.name}-${index}`}
                          className={row.isCurrentUser ? "leaderboard-popup__row--current" : ""}
                        >
                          <td>#{row.rank || index + 1}</td>
                          <td className="leaderboard-popup__identity">{row.name}</td>
                          <td>{row.level}</td>
                          <td>{row.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!isCurrentUserInLeaderboard && (
                  <p className="leaderboard-popup__empty">
                    Your Rank is: {currentUserRank ? `#${currentUserRank}` : "Not available"}
                  </p>
                )}
              </>
            ) : (
              <div className="leaderboard-popup__table-wrap">
                <table className="leaderboard-popup__table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Player</th>
                      <th>Level</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="leaderboard-popup__row--current">
                      <td>#1</td>
                      <td className="leaderboard-popup__identity">{leaderboardIdentity}</td>
                      <td>{currentLevel}</td>
                      <td>{elapsedTime}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default GameControls;
